/**
 * Per-tenant feature-flag hooks. Backend resolves flags server-side and
 * ships them on every auth response (/auth/login, /auth/refresh, /auth/me),
 * stored in the auth store's `user.features`.
 *
 * A missing key means "the backend catalog doesn't know about this feature"
 * — we default to `true` in that case so a mid-deploy where frontend knows
 * about a new feature but backend hasn't shipped the catalog update yet
 * doesn't accidentally hide a screen the user has been using.
 */

import { useAuthStore } from '@/features/auth/store';

export type FeatureKey =
  | 'payroll'
  | 'credit'
  | 'inventory'
  | 'samples'
  | 'delivery_details_extended';

export function useFeature(key: FeatureKey): boolean {
  const features = useAuthStore((s) => s.user?.features);
  if (!features) return true; // pre-auth or missing — don't hide
  const raw = features[key];
  return typeof raw === 'boolean' ? raw : true;
}

export function useFeatures(): Record<FeatureKey, boolean> {
  const features = useAuthStore((s) => s.user?.features) ?? {};
  return {
    payroll: features.payroll !== false,
    credit: features.credit !== false,
    inventory: features.inventory !== false,
    samples: features.samples !== false,
    delivery_details_extended: features.delivery_details_extended !== false,
  };
}
